import type { FormEvent } from 'react';
import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook';

import type { TWFontSizeProp } from '@fragment/ui/src/types/tailwind';

import { ResponsiveOffset } from '../ResponsiveOffset/ResponsiveOffset';

const clickIsWithin = (element: HTMLElement, event: MouseEvent) => {
  const rect = element.getBoundingClientRect();
  return (
    event.clientX > rect.left &&
    event.clientX < rect.right &&
    event.clientY > rect.top &&
    event.clientY < rect.bottom
  );
};

// wrapping the entire modal in a form can cause unexpected behavior
// if the children are not intended to be inside a form
const ModalWrapper = ({
  onSubmit,
  children,
}: {
  onSubmit?: (e: FormEvent) => void;
  children: React.ReactNode;
}) => {
  if (onSubmit) {
    return <form onSubmit={onSubmit}>{children}</form>;
  }
  return <>{children}</>;
};

export type ModalWidth = 'sm' | 'md' | 'lg';

export type ModalProps<FontSizeProp> = {
  onClose: () => void;
  onModalWindowClose?: VoidFunction;
  onSubmit?: (e: FormEvent) => void;
  menu?: React.ReactNode;
  body: React.ReactNode;
  header?: React.ReactNode;
  backDropBlur?: boolean;
  footer?: React.ReactNode;
  'data-testid'?: string;
  fontSize?: TWFontSizeProp<FontSizeProp>;
  shouldCenter?: boolean;
  width?: ModalWidth;
  topBanner?: string;
};

export const Modal = <FontSizeProp,>({
  onClose,
  onSubmit,
  menu,
  header,
  footer,
  body,
  backDropBlur = true,
  'data-testid': dataTestId,
  fontSize = 'text-md' as TWFontSizeProp<FontSizeProp>,
  shouldCenter = true,
  width = 'md',
  topBanner,
}: ModalProps<FontSizeProp>) => {
  useHotkeys('esc', onClose, {
    enableOnFormTags: true,
    scopes: ['modal'],
  });
  const { enableScope, disableScope } = useHotkeysContext();
  useEffect(() => {
    enableScope('modal');
    return () => {
      disableScope('modal');
    };
  }, [disableScope, enableScope]);

  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalContentRef.current &&
        !modalContentRef.current.contains(event.target as Node) &&
        !clickIsWithin(modalContentRef.current, event)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const footerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (footerRef.current) {
      if (footerRef.current.childElementCount === 0) {
        // Collapse the footer if it has no children
        footerRef.current.parentElement?.setAttribute('style', 'display: none');
      } else {
        footerRef.current.parentElement?.setAttribute(
          'style',
          'display: block'
        );
      }
    }
    // Don't remove the dependency on footer. We need this useEffect to run when footer changes.
  }, [footerRef, footer]);

  return createPortal(
    <ModalWrapper onSubmit={onSubmit}>
      <div
        data-testid={dataTestId}
        className={clsx('relative z-30', fontSize)}
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          data-testid="modal-bg"
          className={clsx(
            `fixed inset-0`,
            backDropBlur && 'bg-main opacity-[.15]'
          )}
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-scroll scrollbar-hide flex justify-center">
          <div className="relative top-0">
            <ResponsiveOffset
              verticalOffset="max-h-f12"
              shouldCenter={shouldCenter}
            >
              {topBanner && (
                <div className="bg-black h-[25px] text-white text-center flex items-center justify-center mx-f2">
                  <p className="text-md font-medium">{topBanner}</p>
                </div>
              )}
              <div
                ref={modalContentRef}
                data-testid="modal-parent-div"
                className={clsx(
                  'flex flex-col',
                  'fragment-modal-content', // This is used to identify when a modal is open
                  'bg-canvas h-fit',
                  'w-[calc(100dvw-4ch)] tablet:w-[calc(100dvw-8ch)]',
                  width === 'sm' &&
                    'laptop:w-[40dvw] largeLaptop:w-[40dvw] desktop:w-[33dvw]',
                  width === 'md' &&
                    'laptop:w-[60dvw] largeLaptop:w-[66dvw] desktop:w-[50dvw]',
                  width === 'lg' &&
                    'laptop:w-[calc(100dvw-8ch)] desktop:w-[85dvw]',
                  'mx-f2 border-main-200'
                )}
              >
                <div className="flex w-full h-full p-f2">
                  {menu && (
                    <div
                      className="flex flex-col min-h-full w-[25%] max-w-[20ch] border-r-[1px] border-main-200"
                      data-testid="modal-side-menu"
                    >
                      {menu}
                    </div>
                  )}
                  <div
                    className={clsx([
                      'flex flex-col items-center text-center max-h-full',
                      menu ? 'w-[75%] flex-grow ' : 'w-full',
                    ])}
                    data-testid="modal-content"
                  >
                    {header && (
                      <header
                        className={clsx(
                          'w-full place-self-start text-left h-f2'
                        )}
                      >
                        {header}
                      </header>
                    )}

                    <main
                      className={clsx([
                        header ? 'mt-f4' : '',
                        footer ? 'pb-f2' : '',
                        'relative transform bg-canvas text-left transition-all w-full h-full max-h-full',
                      ])}
                    >
                      {body}
                    </main>
                  </div>
                </div>
                {footer && (
                  <>
                    <footer className={clsx('w-full')}>
                      <hr className="border-main-200 w-full" />
                      <div
                        ref={footerRef}
                        id="footer-container"
                        className="w-full flex space-y-f2 p-f2"
                      >
                        {footer}
                      </div>
                    </footer>
                  </>
                )}
              </div>
            </ResponsiveOffset>
          </div>
        </div>
      </div>
    </ModalWrapper>,
    document.body
  );
};
